import { GridRowsProp } from "@mui/x-data-grid";
import { motion } from "motion/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import StatusFilterItem from "@/components/table/StatusFilterItem";
import { TaskStatuses } from "@/constants/status";
import { taskStatuses } from "@/constants/taskStatuses";
import { useTrackingActions, useTrackingFilters } from "@/stores/use-tracking-table";
import { Task } from "@/types/tasks.types";

export function TrackingStatusFilter({ allRows }: { allRows: GridRowsProp }) {
    const { t } = useTranslation();
    const filters = useTrackingFilters();
    const { updateFilters } = useTrackingActions();

    const activeStyle = useMemo(() => {
        for (const [index, item] of taskStatuses.entries()) {
            if (item.status === filters.status) {
                return { translateX: `${index * 100}%`, backgroundColor: item.hover };
            }
        }
        return false;
    }, [filters]);

    const vals = useMemo(
        () =>
            allRows.reduce(
                (prev, curr) => {
                    const value = (curr as Task).status;
                    if (value) prev[value] = (prev[value] || 0) + 1;
                    return prev;
                },
                {} as Record<TaskStatuses, number>
            ),
        [allRows]
    );

    const handleUpdate = useCallback(
        (status: TaskStatuses) => {
            updateFilters({ status: filters.status === status ? null : status });
        },
        [filters, updateFilters]
    );

    const filterElements = useMemo(
        () =>
            taskStatuses.map((item) => (
                <StatusFilterItem<TaskStatuses>
                    key={item.status}
                    {...item}
                    value={item.status}
                    tooltip={item.tooltip}
                    active={item.status === filters.status}
                    count={vals[item.status] || 0}
                    handleUpdate={handleUpdate}
                />
            )),
        [handleUpdate, vals, filters, t]
    );

    return (
        <div className="relative py-1 grid grid-cols-6 bg-gray-100/80 divide-x divide-gray-300 rounded">
            {filterElements}
            {activeStyle && (
                <motion.div
                    layoutId="tracking-status-filter"
                    id="tracking-status-filter"
                    animate={activeStyle}
                    transition={{ type: "tween" }}
                    className="rounded-none absolute !self-center !h-[calc(100%-8px)] !w-1/6"
                />
            )}
        </div>
    );
}
